/**
 * HomeFeaturedEditorial — Phase M
 *
 * Surfaces 2 hero product editorials on the homepage. Pulls from
 * /api/home/featured-editorials which rotates the picks every ISO week.
 * Renders blurb teaser + image + price + CTA into the product page so
 * Google's crawler discovers the deep Phase-K editorial pages and
 * mid-funnel users land straight on a checkout-ready product.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HomeFeaturedEditorial() {
    const [data, setData] = useState(null);

    useEffect(() => {
        fetch(`${API}/home/featured-editorials`)
            .then((r) => r.json())
            .then((d) => setData(d))
            .catch(() => {});
    }, []);

    const items = data?.items || [];
    if (items.length === 0) return null;

    return (
        <section
            data-testid="home-featured-editorial"
            className="bg-white border-b border-zen-line py-14 sm:py-16"
        >
            <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-2 inline-flex items-center gap-2">
                            <Sparkles size={12} /> Editor&apos;s pick · this week
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl text-zen-ink font-light leading-tight">
                            Two products worth a deeper read
                        </h2>
                    </div>
                    <Link
                        to="/locations/queens-long-island"
                        data-testid="home-featured-see-menu"
                        className="text-[11px] uppercase tracking-[0.25em] text-zen-olive hover:text-zen-olive-dark inline-flex items-center gap-1"
                    >
                        See full menu <ArrowRight size={11} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {items.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/menu/${p.store_slug}/${p.slug}`}
                            data-testid={`home-featured-${p.slug}`}
                            className="group flex gap-5 bg-zen-bg border border-zen-line p-5 hover:border-zen-olive transition"
                        >
                            <div className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 bg-white border border-zen-line overflow-hidden">
                                {p.image ? (
                                    <img
                                        src={p.image}
                                        alt={p.name}
                                        loading="lazy"
                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-zen-line" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                {p.brand && (
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-1 truncate">
                                        {p.brand}
                                    </p>
                                )}
                                <h3 className="font-serif text-lg sm:text-xl text-zen-ink leading-snug mb-1 line-clamp-2">
                                    {p.name}
                                </h3>
                                {typeof p.price === "number" && p.price > 0 && (
                                    <p className="text-[13px] text-zen-ink-soft mb-2">
                                        From <strong>${p.price.toFixed(2)}</strong>
                                    </p>
                                )}
                                <p className="text-[13px] text-zen-ink-soft leading-relaxed line-clamp-3">
                                    {p.blurb}
                                </p>
                                <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-zen-olive inline-flex items-center gap-1">
                                    Read editorial <ArrowRight size={11} />
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
